import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { useAuthStore } from '@core/store/authStore';
import { listFavoriteItems, updateFavoriteItem } from '@core/services/favoriteService';
import {
  isVariantProductOutOfStock,
  isVariantSellable,
  normalizeVariantSkus,
} from '@core/utils/variantSkus';
import { hasProductOptions } from '@core/utils/productOptions';

const STORAGE_KEY = 'bytz_wishlist_v1';

function sameId(a, b) {
  return String(a ?? '') === String(b ?? '');
}

function pickWishlistImage(source = {}) {
  const imageList = [...(Array.isArray(source.images) ? source.images : [])];
  const defaultImage = imageList.find((image) => image?.is_default) ?? imageList[0];

  return (
    source.online_image?.url ??
    defaultImage?.url ??
    defaultImage?.image_url ??
    defaultImage ??
    undefined
  );
}

function pickWishlistUom(source = {}) {
  return (
    source.uom_details?.name ??
    source.uom_details?.short_code?.toUpperCase?.() ??
    source.uom_details?.integer_place_unit ??
    source.unit ??
    source.UOM ??
    (typeof source.uom === 'string' && !source.uom.startsWith('UOM-') ? source.uom : undefined)
  );
}

function firstNumber(...values) {
  for (const value of values) {
    if (value === undefined || value === null || value === '') continue;
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return undefined;
}

function toBoolean(value) {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'string') return value.trim().toLowerCase() === 'true';
  return Boolean(value);
}

function toWishlistItem(product = {}) {
  const source = product.item ?? product;
  const id = source.id ?? source.item_id;
  const availableStock = firstNumber(
    source.locations?.[0]?.available_quantity,
    source.online_available_stock,
    source.stock,
  );
  const onlineTrackInventory = toBoolean(
    source.locations?.[0]?.online_track_inventory ?? source.online_track_inventory,
  );
  const isOutOfStock = isVariantSellable(source)
    ? isVariantProductOutOfStock(
        Array.isArray(source.variant_skus)
          ? normalizeVariantSkus(source.variant_skus)
          : (source.variantSkus ?? null),
      )
    : onlineTrackInventory && availableStock !== undefined && availableStock <= 0;
  const hasOptions = hasProductOptions(source) || Boolean(source.hasOptions);

  return {
    id,
    name: source.item_name ?? source.name ?? '',
    shortDescription:
      source.shortDescription ?? source.short_description ?? source.ShortDescription ?? '',
    price: source.price ?? source.selling_price ?? 0,
    mrp: source.mrp ?? source.MRP ?? null,
    imageUrl: pickWishlistImage(source),
    uom: pickWishlistUom(source),
    uomId: source.uom_id ?? source.uomId,
    tags: source.tags ?? source.Tags ?? [],
    isAvailable:
      !isOutOfStock &&
      (source.isAvailable ??
        source.online_available ??
        source.OnlineAvailability ??
        source.OnlineAvailable ??
        true),
    isOutOfStock,
    availableStock,
    onlineTrackInventory,
    hasOptions,
    hasVariants: source.hasVariants ?? source.has_variants,
    hasModifiers: source.hasModifiers ?? source.has_modifiers,
    hasCombo: source.hasCombo ?? source.has_combo,
    variant_groups: source.variant_groups ?? source.variantGroups,
    modifier_groups: source.modifier_groups ?? source.modifierGroups,
    combo_groups: source.combo_groups ?? source.comboGroups,
    recipe: source.recipe,
    recipe_details: source.recipe_details,
    alternative_units_and_conversions:
      source.alternative_units_and_conversions ?? source.alternativeUnitsAndConversions,
  };
}

export const useWishlistStore = create(
  persist(
    (set, get) => ({
      items: [],

      has: (id) => get().items.some((item) => sameId(item.id, id)),

      setItems: (items) => set({ items }),

      syncFromServer: async () => {
        if (!useAuthStore.getState().isAuthenticated()) return;
        const raw = await listFavoriteItems();
        const mapped = raw.map(toWishlistItem).filter((item) => Boolean(item.id));
        set({ items: mapped });
      },

      toggle: async (product) => {
        const item = toWishlistItem(product);
        if (!item.id) return;
        const prev = get().items;
        const exists = prev.some((entry) => sameId(entry.id, item.id));
        const next = exists ? prev.filter((entry) => !sameId(entry.id, item.id)) : [...prev, item];
        set({ items: next });

        if (!useAuthStore.getState().isAuthenticated()) return;

        try {
          await updateFavoriteItem({
            operation: exists ? 'remove' : 'add',
            itemId: item.id,
          });
        } catch {
          set({ items: prev });
        }
      },

      remove: async (id) => {
        const prev = get().items;
        set((state) => ({ items: state.items.filter((item) => !sameId(item.id, id)) }));

        if (!useAuthStore.getState().isAuthenticated()) return;
        try {
          await updateFavoriteItem({ operation: 'remove', itemId: id });
        } catch {
          set({ items: prev });
        }
      },
      clear: () => set({ items: [] }),
    }),
    {
      name: STORAGE_KEY,
      storage: createJSONStorage(() => localStorage),
    },
  ),
);
